import { NavLink } from "react-router-dom";
import { PageShell, PageHero } from "@/components/marketing/PageShell";
import { LEGAL, LEGAL_INDEX } from "@/content/legal";

function LegalSideNav({ current }) {
  return (
    <nav data-testid="legal-sidenav" className="lg:sticky lg:top-28">
      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Legal</p>
      <ul className="space-y-1">
        {LEGAL_INDEX.map((it) => (
          <li key={it.slug}>
            <NavLink
              to={`/${it.slug}`}
              data-testid={`legal-nav-${it.slug}`}
              className={({ isActive }) =>
                `block rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive || current === it.slug
                    ? "bg-sema-mid/10 font-medium text-foreground"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`
              }
            >
              {it.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// Generic legal document page (Privacy, Terms, DPDP, Data Privacy, EULA, DPA, Subprocessors).
export default function LegalPage({ slug }) {
  const doc = LEGAL[slug];
  if (!doc) return null;
  return (
    <PageShell testid={`legal-${slug}-root`} seo={{
      title: `${doc.title} — Sema`,
      description: doc.intro,
      path: `/${slug}`,
    }}>
      <PageHero testid={`legal-${slug}-h1`} eyebrow="Legal" title={doc.title}
        subtitle={`Last updated ${doc.updated}.`} />

      <section className="pb-28">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 lg:grid-cols-[220px_1fr] lg:gap-16">
          <LegalSideNav current={slug} />

          <div className="min-w-0 max-w-3xl font-body">
            <p className="text-base leading-relaxed text-muted-foreground">{doc.intro}</p>

            {doc.sections && (
              <div className="mt-10 space-y-10">
                {doc.sections.map((s) => (
                  <div key={s.h} className="scroll-mt-28">
                    <h2 className="font-display text-lg font-semibold tracking-tight">{s.h}</h2>
                    <ul className="mt-3 space-y-2.5">
                      {s.p.map((para, i) => (
                        <li key={i} className="text-sm leading-relaxed text-muted-foreground">{para}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {doc.table && (
              <div className="mt-10 overflow-hidden rounded-2xl border border-border/60">
                <table className="w-full text-left text-sm">
                  <thead className="bg-card/60">
                    <tr className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      <th className="px-5 py-3">Subprocessor</th>
                      <th className="px-5 py-3">Purpose</th>
                      <th className="px-5 py-3">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {doc.table.map((r) => (
                      <tr key={r.name} className="text-foreground/90">
                        <td className="px-5 py-3.5 font-medium">{r.name}</td>
                        <td className="px-5 py-3.5 text-muted-foreground">{r.purpose}</td>
                        <td className="px-5 py-3.5 text-muted-foreground">{r.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {doc.note && <p className="mt-8 text-sm leading-relaxed text-muted-foreground">{doc.note}</p>}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
